import { type ReactNode } from 'react';

import { QueryClientProvider } from '@tanstack/react-query';
import { useSelectIsLogged, useSelectLoginState } from '@treneo/features/auth';
import {
  selectDateTimePickerSettings,
  selectTagColorMode,
  selectTagStyling,
  selectTagVariant,
  useConfigUiStore
} from '@treneo/features/config';
import {
  LanguageSync,
  useCurrentLanguage,
  useSaveLanguage,
  useSelectCurrentUserInfo,
  useSelectUserTheme
} from '@treneo/features/user';
import { supportedLanguages } from '@treneo/shared/constants';
import {
  DateTimeConfigContext,
  LoginStateContext,
  SharedAuthContext,
  TagConfigContext
} from '@treneo/shared/contexts';
import { i18next, LanguageContext } from '@treneo/shared/i18n';
import { ThemeContext } from '@treneo/shared/themes';

import { queryClient } from './query-client';

interface ProvidersProps {
  children: ReactNode;
}

const DateTimeConfigBridge = ({ children }: { children: ReactNode }) => {
  const dateTimeSettings = useConfigUiStore(selectDateTimePickerSettings);
  return (
    <DateTimeConfigContext.Provider value={dateTimeSettings}>
      {children}
    </DateTimeConfigContext.Provider>
  );
};

const TagConfigBridge = ({ children }: { children: ReactNode }) => {
  const tagVariant = useConfigUiStore(selectTagVariant);
  const tagColorMode = useConfigUiStore(selectTagColorMode);
  const tagStyling = useConfigUiStore(selectTagStyling);
  const tagConfigValue = { tagVariant, tagColorMode, tagStyling };
  return <TagConfigContext.Provider value={tagConfigValue}>{children}</TagConfigContext.Provider>;
};

const ThemeBridge = ({ children }: { children: ReactNode }) => {
  const theme = useSelectUserTheme();
  const paletteMode: 'light' | 'dark' = theme === 'dark' ? 'dark' : 'light';
  return <ThemeContext.Provider value={paletteMode}>{children}</ThemeContext.Provider>;
};

const AuthBridge = ({ children }: { children: ReactNode }) => {
  const isLogged = useSelectIsLogged();
  const loginState = useSelectLoginState();
  const userInfo = useSelectCurrentUserInfo();
  const authContextValue = { isLogged, userId: userInfo?.email };
  return (
    <LoginStateContext.Provider value={loginState}>
      <SharedAuthContext.Provider value={authContextValue}>{children}</SharedAuthContext.Provider>
    </LoginStateContext.Provider>
  );
};

const SUPPORTED_LANGUAGES_VALUE = supportedLanguages.map(l => ({
  code: l.code,
  name: l.displayName
}));

const LanguageBridge = ({ children }: { children: ReactNode }) => {
  const currentLanguage = useCurrentLanguage();
  const isLogged = useSelectIsLogged();
  const { mutateAsync: saveLanguageMutation } = useSaveLanguage();

  const handleLanguageChange = async (code: string) => {
    void i18next.changeLanguage(code);
    await saveLanguageMutation({ languageCode: code, isLogged });
  };

  const contextValue = {
    currentLanguage: {
      code: currentLanguage.code,
      name: currentLanguage.displayName
    },
    supportedLanguages: SUPPORTED_LANGUAGES_VALUE,
    changeLanguage: handleLanguageChange
  };

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};

export const Providers = ({ children }: Readonly<ProvidersProps>) => (
  <QueryClientProvider client={queryClient}>
    <LanguageSync>
      <AuthBridge>
        <ThemeBridge>
          <LanguageBridge>
            <DateTimeConfigBridge>
              <TagConfigBridge>{children}</TagConfigBridge>
            </DateTimeConfigBridge>
          </LanguageBridge>
        </ThemeBridge>
      </AuthBridge>
    </LanguageSync>
  </QueryClientProvider>
);
